import { type FC, useEffect, useState } from "react";
import { useLocalParticipant, useRoomContext } from "@livekit/components-react";
import { RoomEvent, Track } from "livekit-client";
import MicIcon from "../../../components/icons/MicIcon";
import MicOffIcon from "../../../components/icons/MicOffIcon";
import CloseIcon from "../../../components/icons/CloseIcon";
import ChatFormVoiceVisualizer from "./ChatFormVoiceVisualizer";
import SwitchingToVoiceModeMessage from "./SwitchingToVoiceModeMessage";
import { Slot, Templated } from "../../../templating";

interface ChatFormVoiceControlsProps {
  onExitVoiceMode: () => void;
}

const ChatFormVoiceControls: FC<ChatFormVoiceControlsProps> = ({ onExitVoiceMode }) => {
  const [isMicActive, setIsMicActive] = useState(true);
  const { localParticipant } = useLocalParticipant();
  const [connected, setConnected] = useState(false);
  const room = useRoomContext();

  useEffect(() => {
    if (localParticipant) {
      const micTrack = localParticipant.getTrackPublication(Track.Source.Microphone);
      if (micTrack) {
        if (isMicActive) {
          micTrack.unmute();
        } else {
          micTrack.mute();
        }
      }
    }
  }, [localParticipant, isMicActive]);

  useEffect(() => {
    const handleConnected = (): void => {
      setConnected(true);
    };

    const handleDisconnected = (): void => {
      onExitVoiceMode();
    };

    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);

    if (room.state === "connected") {
      setConnected(true);
    }

    return () => {
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
    };
  }, [room, onExitVoiceMode]);

  if (!connected) {
    return (
      <Templated id="lastbot-composer-voice">
        <Slot name="recording-state">
          <SwitchingToVoiceModeMessage />
        </Slot>
      </Templated>
    );
  }

  return (
    <Templated id="lastbot-composer-voice">
      <div className="flex p-2">
        {isMicActive ? (
          <button
            type="button"
            onClick={() => setIsMicActive(false)}
            className="bg-lastbot-state-overlay/5 text-lastbot-icon-muted p-2 rounded-full hover:bg-lastbot-state-overlay/10 active:bg-lastbot-state-overlay/15 transition-colors duration-200 ease-in-out cursor-pointer"
          >
            <Slot name="stop-button"><MicIcon /></Slot>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setIsMicActive(true)}
            className="bg-lastbot-danger/10 text-lastbot-danger p-2 rounded-full hover:bg-lastbot-danger/15 active:bg-lastbot-danger/20 transition-colors duration-200 ease-in-out cursor-pointer"
          >
            <Slot name="stop-button"><MicOffIcon /></Slot>
          </button>
        )}
        <div className="flex-1">
          <Slot name="waveform"><ChatFormVoiceVisualizer /></Slot>
        </div>
        <button
          type="button"
          onClick={onExitVoiceMode}
          className="bg-lastbot-state-overlay/5 text-lastbot-icon-muted p-2 rounded-full hover:bg-lastbot-state-overlay/10 active:bg-lastbot-state-overlay/15 transition-colors duration-200 ease-in-out cursor-pointer"
        >
          <Slot name="cancel-button"><CloseIcon /></Slot>
        </button>
      </div>
    </Templated>
  );
};

export default ChatFormVoiceControls;
